import {
  Badge,
  Button,
  Checkbox,
  Input,
  LoadingSpinner,
} from "@/components/ui";
import type { UserAccountStatus } from "@/lib/utils/user-account-status";
import { MultiSelect } from "@/components/ui/FormFields/MultiselectFormField/MultiselectFormField";
import { type PersonnelAssignment } from "@/components/features/matter/information/matter-personnel-assignment-diff";
import type { useUserDirectory } from "@/hooks/use-user-directory";
import { formatUsersName } from "@/lib/utils/format";
import { Search } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

export function MatterPersonnelPeoplePicker({
  directory,
  selectedIds,
  statusFor,
  suggestedRolesByUserId,
  assignments,
  roles,
  profileFor,
  onAdd,
  onRolesChange,
  onRemove,
}: {
  directory: ReturnType<typeof useUserDirectory>;
  selectedIds: string[];
  statusFor: (userId: string) => UserAccountStatus | undefined;
  suggestedRolesByUserId: Map<string, string[]>;
  assignments: PersonnelAssignment[];
  roles: { id: string; label: string }[];
  profileFor: (userId: string) => ReactNode;
  onAdd: (userId: string, roleId?: string) => void;
  onRolesChange: (userId: string, roleIds: string[]) => void;
  onRemove: (userId: string) => void;
}) {
  const [pendingAddUserId, setPendingAddUserId] = useState<string | null>(null);
  const [draftRoles, setDraftRoles] = useState<
    Partial<Record<string, string[]>>
  >({});
  const roleTriggers = useRef<Record<string, HTMLButtonElement | null>>({});
  const listRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const {
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
    selectProps: { onLoadMore },
  } = directory;
  const [initiallySelectedIds] = useState(selectedIds);
  const search = directory.search.trim().toLowerCase();
  const initiallySelected = new Set(initiallySelectedIds);
  const users = directory.users
    .filter(
      (user) =>
        (user.enabled !== false || selectedIds.includes(user.id)) &&
        (!search ||
          `${formatUsersName(user)} ${user.email}`
            .toLowerCase()
            .includes(search)),
    )
    .sort((left, right) => {
      const selectedOrder =
        Number(initiallySelected.has(right.id)) -
        Number(initiallySelected.has(left.id));
      return (
        selectedOrder ||
        (formatUsersName(left) || "").localeCompare(
          formatUsersName(right) || "",
        ) ||
        left.id.localeCompare(right.id)
      );
    });

  useEffect(() => {
    if (
      !hasNextPage ||
      isLoading ||
      isFetchingNextPage ||
      isError ||
      !listRef.current ||
      !loadMoreRef.current
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      { root: listRef.current, rootMargin: "80px" },
    );
    observer.observe(loadMoreRef.current);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isLoading, isFetchingNextPage, isError, onLoadMore]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-border border-b px-5 py-4">
        <div className="relative">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            type="search"
            className="pl-9"
            aria-label="Search people"
            placeholder="Search people..."
            value={directory.search}
            onChange={(event) => {
              directory.setSearch(event.target.value);
            }}
          />
        </div>
      </div>
      <div
        ref={listRef}
        className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2"
      >
        <p className="text-muted-foreground px-3 py-2 text-xs font-medium">
          People
        </p>
        {users.map((user) => {
          const selected = selectedIds.includes(user.id);
          const assignedRoles = assignments
            .filter((item) => item.userId === user.id)
            .map((item) => item.roleId);
          const selectedRoleIds = selected
            ? assignedRoles
            : (draftRoles[user.id] ??
              suggestedRolesByUserId.get(user.id) ??
              []);
          return (
            <div
              key={user.id}
              className={`flex min-h-14 items-center gap-3 rounded-md px-3 py-2 ${selected ? "bg-muted" : "hover:bg-muted/60"}`}
            >
              {profileFor(user.id)}
              <div className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium">
                <span className="truncate">{formatUsersName(user)}</span>
                {statusFor(user.id) && (
                  <Badge variant="muted" className="shrink-0">
                    {statusFor(user.id)}
                  </Badge>
                )}
              </div>
              <MultiSelect
                ref={(element) => {
                  roleTriggers.current[user.id] = element;
                }}
                aria-label={`Matter roles for ${formatUsersName(user)}`}
                options={roles.map((role) => ({
                  value: role.id,
                  label: role.label,
                }))}
                selectedValues={selectedRoleIds}
                setSelectedValues={() => {}}
                onOpenChange={(open) => {
                  if (!open) setPendingAddUserId(null);
                }}
                onValueChange={(roleIds) => {
                  if (selected) {
                    onRolesChange(user.id, roleIds);
                  } else if (pendingAddUserId === user.id && roleIds.length) {
                    onAdd(user.id, roleIds[0]);
                    onRolesChange(user.id, roleIds);
                  } else {
                    setDraftRoles((current) => ({
                      ...current,
                      [user.id]: roleIds,
                    }));
                  }
                  setPendingAddUserId(null);
                }}
                ignoreAllSelected
                showToggleAll={false}
                showChips={false}
                showClearButton={false}
                closeOnSelect
                modalPopover
                placeholder="Select roles"
                className="w-40 shrink-0"
              />
              <Checkbox
                checked={selected}
                aria-label={`${selected ? "Remove" : "Add"} ${formatUsersName(user)}`}
                onCheckedChange={() => {
                  if (selected) {
                    onRemove(user.id);
                  } else if (selectedRoleIds.length || roles.length === 1) {
                    const nextRoles = selectedRoleIds.length
                      ? selectedRoleIds
                      : [roles[0]?.id ?? ""];
                    onAdd(user.id, nextRoles[0]);
                    onRolesChange(user.id, nextRoles);
                  } else {
                    setPendingAddUserId(user.id);
                    roleTriggers.current[user.id]?.click();
                  }
                }}
              />
            </div>
          );
        })}
        {users.length === 0 && !directory.isLoading && (
          <p className="text-muted-foreground px-3 py-6 text-center text-sm">
            No people found.
          </p>
        )}
        {directory.isLoading && users.length === 0 && (
          <p
            className="text-muted-foreground flex items-center justify-center gap-2 px-3 py-6 text-sm"
            role="status"
          >
            <LoadingSpinner className="h-4 w-4" />
            Loading people...
          </p>
        )}
        {directory.isError && (
          <Button variant="ghost" onClick={directory.selectProps.onRetry}>
            Retry loading people
          </Button>
        )}
        {directory.hasNextPage && (
          <div
            ref={loadMoreRef}
            className="py-2 text-center"
            aria-live="polite"
          >
            {directory.isFetchingNextPage && (
              <span className="text-muted-foreground inline-flex items-center gap-2 text-sm">
                <LoadingSpinner className="h-4 w-4" />
                Loading more people...
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
